/**
 * Per-step introspection ("what was the agent told") for the run step popover.
 *
 * Primary path: `buildNodeIntrospectionView` renders a structured view from
 * the node config of the run-pinned workflow snapshot (see
 * `useRunSnapshotNodes`) — title, instructions, and separated input fields,
 * mirroring the node config panel.
 *
 * Fallback path: `parseNodeIntrospection` splits the flat execution-time
 * `metadataJson.nodeIntrospection` string for steps whose node isn't in the
 * snapshot (setup-flow inlined steps, deleted nodes) and legacy runs.
 */
import type { ActionDataRequirement } from '@chatsdet/types';
import type { WorkflowNode } from '../../../../types/workflow';

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;

const scalarToString = (v: unknown): string | null => {
  if (typeof v === 'string') return v.trim().length > 0 ? v : null;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return null;
};

/** One input field of an action node, joined from dataRequirements + inputValues. */
export interface IntrospectionField {
  label: string;
  /** Field identifier — also the downstream `$var.<name>` handle. */
  name: string;
  optional: boolean;
  type: string | null;
  /** Authored value, verbatim — macros like `$EMAIL` / `$var.x` pass through untouched. */
  value: string | null;
  description: string | null;
  example: string | null;
  options: { label: string; value: string }[] | null;
}

/** Structured popover content built from a node's config. */
export interface IntrospectionView {
  title: string;
  instructions: string | null;
  fields: IntrospectionField[];
  expectedOutputs: string[];
}

const buildFields = (data: Record<string, unknown>): IntrospectionField[] => {
  const requirements = Array.isArray(data.dataRequirements)
    ? (data.dataRequirements as ActionDataRequirement[])
    : [];
  const inputValues =
    typeof data.inputValues === 'object' && data.inputValues !== null
      ? (data.inputValues as Record<string, unknown>)
      : {};

  const fields = requirements
    .filter((req): req is ActionDataRequirement => typeof req?.name === 'string')
    .map(
      (req): IntrospectionField => ({
        label: str(req.label) ?? req.name,
        name: req.name,
        optional: req.required === false,
        type: str(req.type),
        value: scalarToString(inputValues[req.name]),
        description: str(req.description),
        example: str(req.exampleValue),
        options:
          Array.isArray(req.options) && req.options.length > 0
            ? req.options.map((opt) => ({ label: opt.label, value: opt.value }))
            : null,
      })
    );

  // Values provided without a matching requirement still reach the agent
  // ("Provided input data") — surface them as bare fields.
  const known = new Set(fields.map((f) => f.name));
  for (const [name, raw] of Object.entries(inputValues)) {
    if (known.has(name)) continue;
    const value = scalarToString(raw);
    if (value === null) continue;
    fields.push({
      label: name,
      name,
      optional: false,
      type: null,
      value,
      description: null,
      example: null,
      options: null,
    });
  }
  return fields;
};

/**
 * Build the structured popover view from a node's config. Only trustworthy
 * when the node comes from the run-pinned snapshot (execution-time truth);
 * returns null for unknown node types or empty config so callers can fall
 * back to the persisted introspection string.
 */
export function buildNodeIntrospectionView(
  node: WorkflowNode | undefined
): IntrospectionView | null {
  if (!node) return null;
  const data = (node.data ?? {}) as Record<string, unknown>;
  const empty = { fields: [], expectedOutputs: [] };

  switch (node.type) {
    case 'navigate': {
      const title = str(data.pageTitle);
      const url = str(data.pageUrl);
      if (!title && !url) return null;
      return { title: (title ?? url) as string, instructions: title ? url : null, ...empty };
    }
    case 'action': {
      const title = str(data.customActionTitle) ?? str(data.actionDescription);
      const description = str(data.actionDescription);
      const instructions = description && description !== title ? description : null;
      const fields = buildFields(data);
      const expectedOutputs = Array.isArray(data.declaredOutputs)
        ? (data.declaredOutputs as unknown[]).filter((o): o is string => typeof o === 'string')
        : [];
      if (!title && !instructions && fields.length === 0) return null;
      return { title: title ?? 'Perform action', instructions, fields, expectedOutputs };
    }
    case 'condition': {
      const condition = str(data.conditionDescription);
      return condition ? { title: condition, instructions: null, ...empty } : null;
    }
    case 'assertion': {
      const condition = str(data.condition);
      return condition ? { title: `Verify ${condition}`, instructions: null, ...empty } : null;
    }
    case 'api_sequence': {
      const description = str(data.description);
      return description ? { title: description, instructions: null, ...empty } : null;
    }
    default:
      return null;
  }
}

export function getNodeIntrospection(node: WorkflowNode | undefined): string | null {
  if (!node) return null;
  const data = (node.data ?? {}) as Record<string, unknown>;

  switch (node.type) {
    case 'navigate': {
      const title = str(data.pageTitle);
      const url = str(data.pageUrl);
      if (!title && !url) return null;
      return [title, url].filter(Boolean).join('\n');
    }
    case 'action': {
      const title = str(data.customActionTitle) ?? str(data.actionDescription);
      const description = str(data.actionDescription);
      const instructions = description && description !== title ? description : null;
      if (!title && !instructions) return null;
      return [title, instructions].filter(Boolean).join('\n\n');
    }
    case 'condition':
      return str(data.conditionDescription);
    case 'assertion':
      return str(data.condition);
    case 'api_sequence':
      return str(data.description);
    default:
      return null;
  }
}

/**
 * Marker that prefixes the inline-assertions block the backend appends to an
 * action node's introspection (see `buildNodeDescription` in the API). The
 * authored assertions already render as their own rows under the step, so the
 * tooltip drops everything from this marker onward for action nodes.
 */
const INLINE_ASSERTIONS_MARKER = 'Inline assertions to resolve';

export interface ParsedNodeIntrospection {
  /** First line — the node summary, shown as the tooltip header. */
  title: string;
  /** Remaining detail, shown muted under the header. Empty when there's none. */
  body: string;
}

/**
 * Split the flat introspection snapshot (`${title}\n\n${description}`, per the
 * API's `buildNodeIntrospection`) into a header + body for a formatted tooltip.
 * For `action` nodes the appended inline-assertions block is stripped — those
 * assertions already render as their own rows below the step.
 */
export function parseNodeIntrospection(
  text: string,
  nodeType?: string
): ParsedNodeIntrospection {
  const normalized = text.replace(/\r\n/g, '\n');
  const newlineIdx = normalized.indexOf('\n');
  const title = (newlineIdx === -1 ? normalized : normalized.slice(0, newlineIdx)).trim();
  let body = (newlineIdx === -1 ? '' : normalized.slice(newlineIdx + 1)).trim();

  if (nodeType === 'action') {
    const markerIdx = body.indexOf(INLINE_ASSERTIONS_MARKER);
    if (markerIdx !== -1) body = body.slice(0, markerIdx).trim();
  }

  return { title, body };
}
